// src/frontend_react/src/pages/UserDashboard/ProcessingStep.js
import React, { useState, useEffect, useRef } from 'react';
import Papa from 'papaparse';

// UI Components
import Typography from '../../components/ui/Typography/Typography';
import Stack from '../../components/ui/Stack/Stack';
import Spinner from '../../components/ui/Spinner/Spinner';
import Alert from '../../components/ui/Alert/Alert';
import Box from '../../components/ui/Box/Box';

// Utils
import { generateDeidentifiedFilenameForClient } from '../../utils/filenameHelper'; 
import { parseSelectedFile } from '../../utils/fileParser';
import { cleanMalaysianIC } from '../../utils/icProcessor';
import {
  hmacSha256,
  calculateStringHash,
  aesEncryptData,
} from '../../utils/cryptoHelper';

import styles from './ProcessingStep.module.css';

function ProcessingStep({
  fileDetails,
  config,
  appConfig,
  onSuccess,
  onError,
  disabled,
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState(
    'Initializing data preparation...',
  );
  const [detailedStatus, setDetailedStatus] = useState(
    'Please wait while your file is being processed securely.',
  );
  const [hasError, setHasError] = useState(false);
  const [progress, setProgress] = useState(0);
  const [internalError, setInternalError] = useState(null); // Retained for debugging
  const isMounted = useRef(true);

  const setInternalStatus = (msg, detailMsg = null) => {
    if (isMounted.current) {
      setStatusMessage(msg);
      if (detailMsg) {
        setDetailedStatus(detailMsg);
      }
    }
  };

  useEffect(() => {
    isMounted.current = true;
    setIsLoading(true);
    setHasError(false);
    setInternalError(null);
    setStatusMessage('Initializing data preparation...');
    setDetailedStatus(
      'Please wait while your file is being processed securely.',
    );
    setProgress(0);

    const processBatchOfFiles = async () => {
      if (disabled) {
        onError({ message: 'Processing is disabled by the parent component.' });
        return;
      }
      if (!Array.isArray(fileDetails) || fileDetails.length === 0) {
        onError({ message: 'No files were provided for processing.' });
        return;
      }
      if (
        !config ||
        !appConfig ||
        !appConfig.projectId ||
        !appConfig.hashingKey ||
        !appConfig.outputEncryptionKey
      ) {
        const missingKey = !appConfig.hashingKey
          ? 'Ephemeral Hashing Key'
          : 'Output Encryption Key';
        onError({
          message: `Critical configuration missing (${missingKey}). Cannot proceed securely.`,
        });
        return;
      }

      try {
        let allProcessedRows = [];
        let allSkippedRecords = [];
        let combinedOriginalFileHashes = [];
        let finalHeadersSet = new Set();

        if (config.requires_ic_for_hashing) {
          finalHeadersSet.add('Hashed_ID');
        }

        const totalFiles = fileDetails.length;
        for (let fileIndex = 0; fileIndex < totalFiles; fileIndex++) {
          const detail = fileDetails[fileIndex];
          if (!isMounted.current) return;
          setInternalStatus(
            `Processing file ${fileIndex + 1} of ${totalFiles}`,
            detail.file.name,
          );

          const parsedFileResult = await parseSelectedFile(detail.file);
          const parsedData = parsedFileResult.data;
          const originalHeaders = parsedFileResult.meta.fields;

          // ✅ ADDED: Empty file detection from v2
          if (parsedData.length === 0 && originalHeaders.length === 0) {
            setInternalStatus(
              `Warning: Skipped empty file`,
              `File '${detail.file.name}' is empty and will be skipped.`,
            );
            combinedOriginalFileHashes.push(detail.hash); // Still include hash for manifest
            continue; // Move to the next file
          }
          if (parsedData.length > 0 && originalHeaders.length === 0) {
            throw new Error(
              `In file '${detail.file.name}': Parsed file data exists, but no headers were extracted. Please ensure the first row is a valid header row.`,
            );
          }

          combinedOriginalFileHashes.push(detail.hash);

          for (const row of parsedData) {
            let processedRowOutput = {};
            let skipReason = null;

            if (config.requires_ic_for_hashing) {
              if (
                config.icColumnName &&
                originalHeaders.includes(config.icColumnName)
              ) {
                const rawIcValue = row[config.icColumnName];
                if (
                  rawIcValue === undefined ||
                  rawIcValue === null ||
                  String(rawIcValue).trim() === ''
                ) {
                  skipReason = `Missing or empty IC value in column '${config.icColumnName}'`;
                } else {
                  const cleanedIC = cleanMalaysianIC(String(rawIcValue));
                  if (cleanedIC && appConfig.hashingKey) {
                    try {
                      const hashedId = await hmacSha256(
                        cleanedIC,
                        appConfig.hashingKey,
                      );
                      processedRowOutput['Hashed_ID'] = hashedId;
                    } catch (hashError) {
                      skipReason = `Hashing failed for IC: ${hashError.message}`;
                    }
                  } else if (!cleanedIC) {
                    // ✅ ADDED: Verbose error message from v2
                    skipReason = `Invalid IC format for value '${String(
                      rawIcValue,
                    ).substring(0, 20)}${
                      String(rawIcValue).length > 20 ? '...' : ''
                    }'`;
                  }
                }
              } else if (config.icColumnName) {
                skipReason = `Configured IC Column '${config.icColumnName}' not found in file headers.`;
              }
            }

            if (skipReason) {
              allSkippedRecords.push({
                original_row: { ...row, __source_file: detail.file.name },
                reason: skipReason,
              });
            } else {
              for (const header of originalHeaders) {
                if (
                  config.requires_ic_for_hashing &&
                  config.icColumnName &&
                  header === config.icColumnName
                )
                  continue;
                if (
                  Array.isArray(config.piiColumnsToRemove) &&
                  config.piiColumnsToRemove.includes(header)
                )
                  continue;

                const analyticalColumnsToKeep =
                  config.analyticalColumnsToKeep || [];
                if (
                  analyticalColumnsToKeep.length === 0 ||
                  analyticalColumnsToKeep.includes(header)
                ) {
                  processedRowOutput[header] = row[header];
                  finalHeadersSet.add(header);
                }
              }
              allProcessedRows.push(processedRowOutput);
            }
          }
          if (isMounted.current)
            setProgress(10 + Math.floor(((fileIndex + 1) / totalFiles) * 70));
        }

        if (!isMounted.current) return;
        setInternalStatus(
          `All files processed. Generating final dataset...`,
          `Combined ${allProcessedRows.length} valid records from ${totalFiles} file(s).`,
        );

        let orderedFinalHeaders = Array.from(finalHeadersSet);
        if (orderedFinalHeaders.includes('Hashed_ID')) {
          orderedFinalHeaders = [
            'Hashed_ID',
            ...orderedFinalHeaders.filter((h) => h !== 'Hashed_ID').sort(),
          ];
        } else {
          orderedFinalHeaders.sort();
        }

        const finalCsvString =
          allProcessedRows.length > 0
            ? Papa.unparse(allProcessedRows, {
                columns: orderedFinalHeaders,
                header: true,
              })
            : orderedFinalHeaders.length > 0
            ? orderedFinalHeaders.join(',')
            : '';

        if (!isMounted.current) return;
        setProgress(85);
        setInternalStatus('Calculating data hash...');

        const processedFileContentHash = await calculateStringHash(
          finalCsvString,
        );

        if (!isMounted.current) return;
        setProgress(90);
        setInternalStatus('Encrypting processed data (AES-256)...');

        const encryptedDataBuffer = await aesEncryptData(
          new TextEncoder().encode(finalCsvString).buffer,
          appConfig.outputEncryptionKey,
        );
        const encryptedDataBlob = new Blob([encryptedDataBuffer], {
          type: 'application/octet-stream',
        });

        if (!isMounted.current) return;
        setProgress(95);
        setInternalStatus('Preparing manifest...');

        const manifestTimestamp = new Date()
          .toISOString()
          .replace(/[-:.]/g, '')
          .slice(0, 14);
        const encryptedOutputFilename = generateDeidentifiedFilenameForClient(
            appConfig.projectId,
            appConfig.batchId,
            config.sourceType
        );

        const localAppManifest = {
          // Top-level fields required by the backend
          original_filename: fileDetails[0]?.file.name || 'batch_upload.dat', // Add a fallback filename
          encrypted_filename_on_disk: encryptedOutputFilename,
          deidentified_rows: allProcessedRows.length,
          skipped_rows: allSkippedRecords.length,
          batch_id: appConfig.batchId,

          // Additional contextual information
          success: true,
          message: 'File batch processed and encrypted locally.',
          original_filenames: fileDetails.map((d) => d.file.name),
          original_file_hashes: combinedOriginalFileHashes,
          processed_format: config.outputFormat,
          raw_file_deleted_from_client_confirmed: config.deleteOriginalFile,

          // Nested metadata object
          file_metadata: {
            source_type: config.sourceType,
            processed_file_content_hash: processedFileContentHash,
            original_header_set:
              fileDetails.length > 0 ? fileDetails[0].headers : [],
            final_columns_in_this_file: orderedFinalHeaders,
            skipped_records_preview: allSkippedRecords
              .slice(0, 5)
              .map((sr) => ({
                reason: sr.reason,
                source_file: sr.original_row.__source_file,
                record_preview: Object.fromEntries(
                  Object.entries(sr.original_row).slice(0, 3),
                ),
              })),
          },
        };
        if (!isMounted.current) return;
        setProgress(100);
        setInternalStatus('Local processing complete!');

        onSuccess({ encryptedDataBlob }, localAppManifest);
      } catch (error) {
        if (!isMounted.current) return;
        console.error('Local processing error:', error);
        const errorMessage =
          error.message || 'An unknown error occurred during local processing.';
        setInternalError(error); // Retain for better debugging
        setHasError(true);
        setStatusMessage('Error: Processing Failed');
        setDetailedStatus(errorMessage);
        onError({
          message: errorMessage,
          details: error.stack || error.toString(),
        });
      } finally {
        if (isMounted.current) setIsLoading(false);
      }
    };

    processBatchOfFiles();

    return () => {
      isMounted.current = false;
    };
  }, [fileDetails, config, appConfig, onSuccess, onError, disabled]);

  let statusMessageColor = 'primary';
  if (hasError) {
    statusMessageColor = 'error';
  } else if (!isLoading && !hasError) {
    statusMessageColor = 'success';
  }

  return (
    <Box className={styles.processingStepContainer} py="xl">
      <Stack spacing="lg" alignItems="center" justifyContent="center">
        {isLoading && (
          <>
            <Spinner size="lg" />
            <Typography
              variant="h4"
              mt="md"
              color={statusMessageColor}
              align="center"
            >
              {statusMessage}
            </Typography>
            <progress
              value={progress}
              max="100"
              style={{
                width: '80%',
                accentColor: 'var(--cipher-color-primary-action)',
              }}
            />
            <Typography
              variant="body1"
              color="secondary"
              align="center"
              className={styles.detailedStatusOverride}
            >
              {detailedStatus}
            </Typography>
          </>
        )}
        {!isLoading && hasError && (
          <Alert variant="error" title="Processing Error">
            {detailedStatus}
          </Alert>
        )}
        {!isLoading && !hasError && (
          <Alert variant="success" title="Processing Complete">
            {statusMessage}
          </Alert>
        )}
      </Stack>
    </Box>
  );
}

export default ProcessingStep;
